import { useEffect, useMemo } from 'react';
import { useForm, Form, Controller } from 'react-hook-form';
import moment from 'moment';
import { Box, Flex, Modal, ModalBody, ModalContent, ModalHeader, ModalOverlay } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { useModal } from 'Store/modal';
import InputTime from 'UiKit/input-time';
import InputComponent from 'UiKit/input';
import InputSelectComponent from 'UiKit/input-select';
import InputDatepicker from 'UiKit/input-datepicker';
import ButtonOutline from 'UiKit/button/buttonOutline';
import ButtonSolid from 'UiKit/button/buttonSolid';
import { TextBold, TextRegular, TextSemiBold } from 'UiKit/typography';
import useGetAllAccountLivestream from 'Domain/schedule/useGetAllAccountLivestream';
import useGetAllStudio from 'Domain/schedule/useGetAllStudio';
import useGetAllOperator from 'Domain/schedule/useGetAllOperator';
import usePostSchedule from 'Domain/schedule/usePostSchedule';
import usePatchSchedule from 'Domain/schedule/usePatchSchedule';
import useGetScheduleHostList from 'Domain/schedule/useGetScheduleHostList';
import { CloseIcon } from 'Asset/icon';

import checkFormValidation from 'View/schedule/helper/checkFormValidation';
import { useScheduleState } from 'View/schedule/state';
import formatEditSetFormUpsert from 'View/schedule/helper/formatEditSetFormUpsert';

import { defaultValues } from './const';

import styles from './style.module.css';

const FormUpsert = () => {
  const { t } = useTranslation('schedule');
  const { state, updateState } = useScheduleState();
  const { watch, control, setValue, register, formState, reset } = useForm({ defaultValues });
  const formField = watch();
  const { errors } = formState;
  const { accountLivestreamId, startedAt, endedAt } = formField;

  const selectedBrand = useMemo(() => {
    return accountLivestreamId?.[0]?.brandName;
  }, [accountLivestreamId]);

  const { mutate: postSchedule } = usePostSchedule({ resetFormData: reset });
  const { mutate: patchSchedule } = usePatchSchedule({ resetFormData: reset });

  const { data: optionsAccountLivestream } = useGetAllAccountLivestream();
  const { data: optionsStudio } = useGetAllStudio();
  const { data: optionsHost } = useGetScheduleHostList({
    payload: {
      brandName: selectedBrand,
      startedAt,
      endedAt,
    },
  });
  const { data: optionsOperator } = useGetAllOperator();

  const { onOpenModal, onResetModal } = useModal();

  const handleSubmit = (form) => {
    const { data, event } = form;
    event.preventDefault();
    if (state?.isEditFormUpsert) patchSchedule({ id: state?.initialSelectedSchedule.id, formData: data });
    else postSchedule(data);
  };

  const minSelectableTime = new Date();
  minSelectableTime.setHours(0, 0, 0);

  const maxSelectableTime = new Date();
  maxSelectableTime.setHours(23, 59, 59);

  const isDisabled = checkFormValidation({ formField });

  const onClose = () => {
    onOpenModal({
      isOpen: true,
      type: 'cancel',
      title: t('cancelModalTitle'),
      contentDescription: t('cancelModalDescription'),
      showPrimaryButton: true,
      primaryButtonText: t('cancelModalPrimaryButton'),
      showSecondaryButton: true,
      secondaryButtonText: t('cancelModalSecondaryButton'),
      onClickPrimaryButton: () => {
        reset();
        onResetModal();
        updateState({ isOpenFormUpsert: false, initialSelectedSchedule: {}, isEditFormUpsert: false, isDialogOpen: false });
      },
    });
  };

  useEffect(() => {
    if (state?.isOpenFormUpsert && state?.initialSelectedSchedule) {
      let { startedAt, endedAt, selectedDate } = state?.initialSelectedSchedule;

      startedAt = startedAt ?? new Date();
      endedAt = endedAt ?? new Date();
      selectedDate = selectedDate ?? new Date();

      const endedAtTime = moment(endedAt).format('HH:mm');

      if (endedAtTime === '00:00') endedAt = new Date(endedAt).setHours(23, 59, 59);

      setValue('selectedDate', new Date(selectedDate));
      setValue('startedAt', new Date(startedAt) ?? new Date());
      setValue('endedAt', new Date(endedAt));

      if (state?.isEditFormUpsert) formatEditSetFormUpsert({ initialSelectedSchedule: state?.initialSelectedSchedule, setValue });
    }
  }, [state?.initialSelectedSchedule, state?.isOpenFormUpsert, setValue, state?.isEditFormUpsert]);

  return (
    <Modal isOpen={state?.isOpenFormUpsert} isCentered onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxWidth={'581px'} paddingBottom="16px">
        <ModalHeader className={styles['form__header']}>
          <TextBold className={styles['form__title-text']}>{t('formTitle')}</TextBold>

          <CloseIcon color="var(--color-semantic-text)" size="22px" onClick={onClose} data-testid="form-upsert__close-button" />
        </ModalHeader>
        <ModalBody className={styles['form__container']}>
          <Form onSubmit={handleSubmit} control={control}>
            <Flex gap={'24px'} flexDirection={'column'}>
              <InputComponent
                className={styles['form__input']}
                label={t('fieldTitle')}
                placeholder={t('fieldTitlePlaceholder')}
                value={formField?.title}
                isRequired
                error={errors.title}
                register={{
                  ...register('title', {
                    required: 'Judul is required!',
                  }),
                }}
              />
              <Controller
                control={control}
                name={'accountLivestreamId'}
                rules={{ required: true }}
                render={({ field }) => (
                  <InputSelectComponent
                    className={styles['form__select']}
                    error={errors.accountLivestreamId}
                    label={t('fieldAccount')}
                    value={formField?.accountLivestreamId}
                    placeholder={`${t('fieldAccountSelect')} ${t('fieldAccount')}`}
                    options={optionsAccountLivestream?.map((account) => ({
                      ...account,
                      isDisabled: !!selectedBrand && account?.brandName !== selectedBrand,
                    }))}
                    isMulti={true}
                    isCloseMenuOnSelect
                    field={{
                      ...field,
                      onChange: (selectedOption) => {
                        field.onChange(selectedOption);
                        if (!selectedOption?.length) setValue('hostId', []);
                      },
                    }}
                    isRequired
                  />
                )}
              />
              <Flex alignItems={'end'}>
                <InputDatepicker
                  placeholder={`${t('fieldAccountSelect')} ${t('time')}`}
                  margin="0 24px 0 0"
                  label={t('time')}
                  onChange={(value) => setValue('selectedDate', value)}
                  value={formField?.selectedDate}
                />
                <InputTime
                  flex={1}
                  value={formField?.startedAt}
                  placeholder="HH:MM"
                  minTime={minSelectableTime}
                  maxTime={formField?.endedAt || maxSelectableTime}
                  error={errors.startedAt}
                  onChange={(value) => setValue('startedAt', value)}
                />
                <TextSemiBold className={styles['divider__text']}>-</TextSemiBold>
                <InputTime
                  flex={1}
                  timeInterval={30}
                  value={formField?.endedAt}
                  minTime={formField?.startedAt || minSelectableTime}
                  maxTime={maxSelectableTime}
                  placeholder="HH:MM"
                  error={errors.endedAt}
                  onChange={(value) => setValue('endedAt', value)}
                />
              </Flex>

              <Box>
                <Box>
                  <TextBold className={styles['form__label']}>
                    {t('fieldHost')} <span className={styles['form__label--required']}>*</span>
                  </TextBold>
                  <TextRegular className={styles['form__description']}>{t('hostConflictWarning')}</TextRegular>
                </Box>
                <Controller
                  control={control}
                  name={'hostId'}
                  rules={{ required: 'Host is required!' }}
                  render={({ field }) => (
                    <InputSelectComponent
                      error={errors.hostId}
                      placeholder={`${t('fieldAccountSelect')} ${t('fieldHost')}`}
                      value={formField?.hostId}
                      options={optionsHost}
                      isMulti={true}
                      isRequired
                      isDisabled={!selectedBrand}
                      field={field}
                      isCloseMenuOnSelect
                    />
                  )}
                />
              </Box>

              <Controller
                control={control}
                name={'studioId'}
                rules={{ required: 'Ruangan is required' }}
                render={({ field }) => (
                  <InputSelectComponent
                    error={errors.studioId}
                    value={formField?.studioId}
                    label={t('fieldRoom')}
                    placeholder={`${t('fieldAccountSelect')} ${t('fieldRoom')}`}
                    options={optionsStudio}
                    field={field}
                    isCloseMenuOnSelect
                    isRequired
                  />
                )}
              />
              <Controller
                name={'operatorId'}
                control={control}
                rules={{ required: 'Operator is required' }}
                render={({ field }) => (
                  <InputSelectComponent
                    error={errors.operatorId}
                    value={formField?.operatorId}
                    label={t('fieldOperator')}
                    placeholder={`${t('fieldAccountSelect')} ${t('fieldOperator')}`}
                    options={optionsOperator}
                    isRequired
                    isMulti={true}
                    field={field}
                    isCloseMenuOnSelect
                  />
                )}
              />

              <Flex gap={'21px'}>
                <ButtonOutline type="button" width={'50%'} onClick={onClose} className={styles['form__button--cancel']} color="#000">
                  {t('cancelButton')}
                </ButtonOutline>
                <ButtonSolid type="submit" width={'50%'} className={styles['form__button--submit']} disabled={isDisabled}>
                  {t('saveButton')}
                </ButtonSolid>
              </Flex>
            </Flex>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default FormUpsert;
